import {useEffect} from "react"
import {batch, useSignal} from "@preact/signals-react"
import {useAppToast} from "@ui-seeker/app"
import {getSubmit} from "@ui-seeker/utils"

import {BACKEND_APIS} from "@constants"
import {isLoadingBatches, batchesTableData, batchStatusFilter, batchOriginFilter, batchCustomerFilter, batchCustomerOptions} from "../@signals"


export default function useBatchesData(autoRefresh = false) {
	const { showToast } = useAppToast()

    const isEmpty = useSignal(false)

	const fetchBatches = (statusFilter, originFilter, customerFilter) => {
		batchesTableData.value = []
		const params = {
			minIaDistance: 0.9,
			statusFilter: statusFilter
		}
		if (originFilter && originFilter !== 'all') params.origin = originFilter
		if (customerFilter && customerFilter !== 'all') params.idCustomer = customerFilter
		getSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches`, params).then(res => {
			if (!res?.length) isEmpty.value = true
			else isEmpty.value = false
			batchesTableData.value = res || []
		}).catch(() => showToast.error()
		).finally(() => {
			isLoadingBatches.value = false
		})
	}

    useEffect(() => {
		if (isLoadingBatches.value) batch(() => {
			fetchBatches(batchStatusFilter.value, batchOriginFilter.value, batchCustomerFilter.value)
		})
	}, [isLoadingBatches.value])

	// The customer list for the toolbar select. Fetched once, not per render: it is a
	// short catalogue and it does not change while the page is open. Deliberately NOT
	// through CustomerAutocomplete — that component writes createBatchFormData.customerID,
	// so mounting it outside the create drawer would edit the batch being created.
	useEffect(() => {
		if (batchCustomerOptions.value.length) return
		getSubmit(`${BACKEND_APIS.BATCH_ADMIN}/customers`).then(res => {
			batchCustomerOptions.value = Array.isArray(res) ? res : []
		}).catch(() => {
			// The filter simply stays on "Any Customer"; the table is unaffected.
		})
	}, [])

	// Reload when the status, origin or customer filter changes
	useEffect(() => {
		isLoadingBatches.value = true
	}, [batchStatusFilter.value, batchOriginFilter.value, batchCustomerFilter.value])

	// Merge new data with existing data, preserving array order for table state
	const mergeTableData = (newData) => {
		const currentData = batchesTableData.value
		if (!currentData?.length) {
			batchesTableData.value = newData
			return
		}

		// Create a map of new data by batchID
		const newDataMap = new Map(newData.map(item => [item.batchID, item]))

		// Update existing items in place, preserving order
		const updatedData = currentData.map(item => {
			const newItem = newDataMap.get(item.batchID)
			if (newItem) {
				newDataMap.delete(item.batchID) // Mark as processed
				return { ...item, ...newItem }
			}
			return null // Item no longer exists (was filtered out)
		}).filter(Boolean)

		// Add any new items at the end
		const newItems = [...newDataMap.values()]
		if (newItems.length > 0) {
			updatedData.push(...newItems)
		}

		batchesTableData.value = updatedData
	}

	// Auto-refresh every 15 seconds
	useEffect(() => {
		if (!autoRefresh) return

		const intervalId = setInterval(() => {
			// The Origin filter is applied SERVER-SIDE, so it has to travel on this
			// request too. Without it the silent refresh fetched every batch and
			// mergeTableData appended the ones the filter had excluded — 15 seconds after
			// picking "Recurrent Only" the table showed manual batches again, under a
			// select that still read "Recurrent Only", and it never recovered because the
			// interval kept re-adding them (filteredBatchesTableData only filters by
			// batchType).
			const params = {
				minIaDistance: 0.9,
				statusFilter: batchStatusFilter.value
			}
			const origin = batchOriginFilter.value
			if (origin && origin !== 'all') params.origin = origin
			const customer = batchCustomerFilter.value
			if (customer && customer !== 'all') params.idCustomer = customer

			// Fetch silently without showing loading state
			getSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches`, params).then(res => {
				if (res?.length) {
					mergeTableData(res)
				}
			}).catch(() => {
				// Silent fail on auto-refresh
			})
		}, 15000) // 15 seconds

		return () => clearInterval(intervalId)
		// The filters are in the deps so the interval is rebuilt with the current values:
		// the closure above captures them, and a stale one re-introduces the same bug.
	}, [autoRefresh, batchStatusFilter.value, batchOriginFilter.value, batchCustomerFilter.value])

	return {
        isEmpty: isEmpty.value
	}
}
